/**
 * Location Usage Multi-Select Component
 * Multi-select dropdown for machine usage type filter
 * Updated per HIVE-6245 requirement #2
 */

import { useState, useCallback } from 'react';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import {
  LocationUsage,
  LocationUsageLabels,
  type LocationUsageType,
} from '../../types/filter-enums';

interface LocationUsageDropdownProps {
  value: LocationUsageType[] | undefined;
  onChange: (value: LocationUsageType[] | undefined) => void;
  onAutoRefresh?: () => void;  // Callback for auto-refresh on change
  className?: string;
}

export function LocationUsageDropdown({
  value = [],
  onChange,
  onAutoRefresh,
  className = '',
}: LocationUsageDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedValues = value || [];

  const handleToggle = useCallback(
    (usage: LocationUsageType) => {
      let newValues: LocationUsageType[];
      if (selectedValues.includes(usage)) {
        newValues = selectedValues.filter((v) => v !== usage);
      } else {
        newValues = [...selectedValues, usage];
      }
      onChange(newValues.length > 0 ? newValues : undefined);
      // Trigger auto-refresh when selection changes
      if (onAutoRefresh) {
        onAutoRefresh();
      }
    },
    [selectedValues, onChange, onAutoRefresh]
  );

  const handleClearAll = useCallback(() => {
    onChange(undefined);
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [onChange, onAutoRefresh]);

  const handleSelectAll = useCallback(() => {
    onChange(Object.values(LocationUsage));
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [onChange, onAutoRefresh]);

  const getDisplayText = () => {
    if (selectedValues.length === 0) return 'All';
    if (selectedValues.length === 1) return LocationUsageLabels[selectedValues[0]];
    if (selectedValues.length === Object.values(LocationUsage).length) return 'All';
    return `${selectedValues.length} selected`;
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Label className="text-xs text-muted-foreground">Location Usage</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 justify-between text-sm font-normal"
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <div className="p-2 border-b">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleClearAll}
              >
                Clear
              </Button>
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {Object.values(LocationUsage).map((usage) => (
              <div
                key={usage}
                className="flex items-center gap-2 py-1.5 px-1 hover:bg-muted rounded cursor-pointer"
                onClick={() => handleToggle(usage)}
              >
                <Checkbox
                  checked={selectedValues.includes(usage)}
                  onCheckedChange={() => handleToggle(usage)}
                />
                <span className="text-sm">{LocationUsageLabels[usage]}</span>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
